import { useAsync } from "@/shared/hooks/HookBase";
import { useCallback } from "react";
import type IIntegrationSetup from "../contracts/IIntegrationSetup";
import IntegrationSetupService from "../services/IntegrationSetupService";
import type IntegrationSetupDto from "../models/IntegrationSetup";
import type IntegrationSetupCreate from "../models/IntegrationSetupCreate";
import type Page from "@/shared/models/Page";

const integrationSetupService: IIntegrationSetup = new IntegrationSetupService();

export function useIntegrationSetups() {
  const { loading, error, execute } = useAsync();

  const getMySetups = useCallback(
    async (
      pageNumber: number,
      pageSize: number,
    ): Promise<Page<IntegrationSetupDto> | void> => {
      return await execute(() =>
        integrationSetupService.GetMySetups(pageNumber, pageSize),
      );
    },
    [execute],
  );

  const getByProvider = useCallback(
    async (providerId: string): Promise<IntegrationSetupDto | null | void> => {
      return await execute(() =>
        integrationSetupService.GetByProvider(providerId),
      );
    },
    [execute],
  );

  const create = useCallback(
    async (
      integrationSetup: IntegrationSetupCreate,
    ): Promise<IntegrationSetupDto | void> => {
      return await execute(
        () => integrationSetupService.Create(integrationSetup),
        { successMessage: "Configuración creada correctamente" },
      );
    },
    [execute],
  );

  const update = useCallback(
    async (
      id: string,
      integrationSetup: IntegrationSetupCreate,
    ): Promise<IntegrationSetupDto | void> => {
      return await execute(
        () => integrationSetupService.Update(id, integrationSetup),
        { successMessage: "Configuración actualizada correctamente" },
      );
    },
    [execute],
  );

  const test = useCallback(
    async (id: string): Promise<void> => {
      return await execute(
        () => integrationSetupService.Test(id),
        { successMessage: "Prueba completada correctamente" },
      );
    },
    [execute],
  );

  const delete_ = useCallback(
    async (id: string): Promise<void> => {
      return await execute(
        () => integrationSetupService.Delete(id),
        { successMessage: "Configuración eliminada correctamente" },
      );
    },
    [execute],
  );

  return {
    loading,
    error,
    getMySetups,
    getByProvider,
    create,
    update,
    test,
    delete: delete_,
  };
}
